<template>
<header class="page-header page-header-fixed" :class="{ 'has-search': searchIsActive }">
  <div class="page-header-section page-header-section-left">
    <nav class="header-menu">
      <ul class="page-header__main-menu-list">
        <li v-if="!isSidebarFixed" class="page-header-sidebar-toggle-wrapper">
          <a href="javascript:;" @click="setSidebarState(true)">
            <i class="fas fa-bars"></i>
          </a>
        </li>
        <li class="page-header-search-wrapper">
          <search :active.sync="searchIsActive" @clear="searchIsActive = false"/>
        </li>
        <li v-for="mainMenuLink in mainMenuLinks" class="page-header-main-menu-item">
          <router-link :to="mainMenuLink.to">{{ mainMenuLink.text }}</router-link>
        </li>
      </ul>
    </nav>
  </div>
  <div class="page-header-section page-header-section-right">
    <nav class="header-menu">
      <ul class="page-header__main-menu-list">
        <li v-if="activeCityPhone" class="page-header-dropdown-wrapper page-header-dropdown-callback-wrapper">
          <callback-form-modal :phone="activeCityPhone">
            <template #toggle>
              <a href="javascript:;">
                <IconPhone class="h-5 fill-current text-primary align-middle"/>
                <span class="page-header-dropdown-label">
                  <span class="page-header-dropdown-label-text">{{ activeCityPhone.national }}</span>
                </span>
              </a>
            </template>
          </callback-form-modal>
        </li>
        <li v-if="displayCitySelect" class="page-header-dropdown-wrapper">
          <HeaderCitySelect
            v-on="citySelectProps.listeners"
            v-bind="citySelectProps.props"
          >
            <template #toggle="{ activeCity }">
              <a href="javascript:;">
                <IconMapMarker class="page-header__city-icon"/>
                <span class="page-header-dropdown-label" v-if="activeCity">
                  <span class="page-header-dropdown-label-text">{{ activeCity.name }}</span>
                </span>
              </a>
            </template>
          </HeaderCitySelect>
        </li>
        <!--
                    <li class="page-header-action-item page-header-action-favorites">
                        <a href="javascript:;" class="page-header-action page-header-action-favorites">
                            <i class="fal fa-heart"></i>
                        </a>
                    </li>
                    -->
        <div class="page-header__main-menu-divider"></div>
        <li v-if="authenticationEnabled" class="page-header-action-item page-header-action-user">
          <HeaderUserModal :authentication-enabled="authenticationEnabled">
            <template #toggle="{ authenticated, active, onClick }">
              <button
                v-if="authenticated"
                class="page-header__user-button"
                @click="onClick"
                type="button"
              >
                <div class="page-header__user-avatar">
                  <responsive-image v-if="avatarMedia" :media="avatarMedia" :sizes="['medium', 'thumb']" alt="Avatar" />
                  <img v-else :src="$store.getters['auth/userAvatar']" alt="Avatar">
                </div>
              </button>
              <button
                v-else
                @click="onClick"
                :class="{ active }"
                class="page-header__login-button"
                type="button"
              >
                <IconUser/>
              </button>
            </template>
          </HeaderUserModal>
        </li>
        <li class="page-header-action-item page-header-action-cart">
          <CartButton
            @click="toggleCartModal"
          />
        </li>
      </ul>
    </nav>
  </div>
</header>
</template>

<script>
import { mapMutations, mapGetters } from 'vuex'
import mixin from './mixin';
import CallbackFormModal from '@/components/modals/CallbackFormModal'
import Search from '@/components/search/Search'
import isScreenConditionTrue from '@/helpers/isScreenConditionTrue';
import HasMainMenuLinks from "@/mixins/HasMainMenuLinks";
import ResponsiveImage from '@/components/images/ResponsiveImage'

// export const height = '56px';
export default {
  mixins: [mixin, HasMainMenuLinks],
  components: {
    ResponsiveImage,
    CallbackFormModal,
    Search,
    HeaderCitySelect: () => import('./HeaderCitySelect'),
    HeaderUserModal: () => import('./HeaderUserModal'),
    CartButton: () => import(/* webpackPreload: true */ '@/components/headers/CartButton'),

    IconUser: () => import('@/assets/images/icons/icon-user.svg?inline'),
    IconPhone: () => import('@/assets/images/icons/icon-phone.svg?inline'),
    IconMapMarker: () => import('@/assets/images/icons/icon-map-marker.svg?inline'),
  },

  computed: {
    ...mapGetters({
      activeCityPhone: 'store/activeCityPhone',
      authenticationEnabled: 'store/authenticationEnabled',
      mainMenuTitles: 'settings/mainMenuTitles',
      avatarUrl: 'auth/userAvatar',
      avatarMedia: 'auth/userAvatarMedia',
    }),
    isSidebarFixed() {
      return isScreenConditionTrue(this.$store, this.$store.state.settings.sidebar.fixed);
    }
  },

  methods: {
    ...mapMutations({
      setSidebarState: 'ui/setSidebarState',
      toggleCartModal: 'ui/toggleCartModal',
    }),
  },
}
</script>

<style lang="less">
@import '~@/assets/less/components/utilities';
@import './common';

.page-header {
  #page-headers-common.header();
  @apply flex bg-white px-1;

  &.page-header-transparent {
    @apply bg-transparent shadow-none;
  }

  .page-header-section {
    @apply w-auto flex;
    transition: all 0.3s ease-out;

    &.page-header-section-left {
      @apply flex-grow;
    }
    &.page-header-section-right {
      @apply justify-end;
    }
  }

  &.has-search {
    .page-header-section-left {
      @apply flex-grow;

      .header-menu {
        @apply flex-grow;

        .page-header__main-menu-list {
          > *:not(.page-header-search-wrapper) {
            @apply hidden;
          }
          .page-header-search-wrapper {
            @apply flex-grow;
            flex-basis: calc(460px + 2 * theme('padding.2'));
          }
        }
      }
    }

    .page-header-section-right {
      @apply hidden;
    }
  }

  nav.header-menu {
    .page-header__main-menu-list {
      @apply h-full flex items-center;
      padding: 0;
      margin: 0;
      list-style-type: none;

      li {
        @apply align-middle;

        &.page-header-sidebar-toggle-wrapper {
          a {
            @apply px-3;
          }
        }

        &.page-header-search-wrapper {
          @apply inline-flex items-center;
          height: var(--header-height);

          .search-component {
            @apply flex-grow;
            .search-component__open-button {
              @apply p-4 text-xl leading-none;
            }
          }
        }

        &.page-header-main-menu-item {
          @apply hidden;

          a {
            @border-width: 3px;
            border-bottom: @border-width solid transparent;
            padding-top: @border-width;

            &.router-link-exact-active {
              @apply bg-navbar-active text-primary;
              border-bottom-color: theme('colors.menu-text-hover');
            }
          }
        }

        a {
          @apply inline-block px-2 text-base flex items-center;
          text-decoration: none;
          transition-timing-function: ease-in;
          transition-property: color, background-color;
          transition-duration: .1s;
          height: var(--header-height);
          color: theme('colors.menu-text');

          &:hover, &.active {
            color: theme('colors.menu-text-hover');
          }
        }

        &.page-header-dropdown-wrapper {
          @apply mx-1;
          .page-header__city-icon {
            @apply h-5 fill-current text-primary;
          }

          .page-header-dropdown-label {
            @apply hidden ml-1;

            .page-header-dropdown-label-text {
              @apply border-b border-dotted border-black border-primary;
            }

            &:hover {
              @apply border-primary;
            }
          }
        }

        &.page-header-action-user {
          @apply w-8 mx-2;
          .page-header__user-button {
            @apply block w-full;
            .page-header__user-avatar {
              .aspect-ratio-image(1);
              img {
                @apply rounded border border-primary object-cover;
              }
            }
          }
          .page-header__login-button {
            @apply text-primary w-full h-8 flex justify-center items-center;
            &.active, &:focus {
              @apply text-menu-text-hover outline-none;
            }
            > svg {
              @apply w-4 fill-current;
              color: inherit;
            }
          }
          .page-header__user-icon {
            @apply h-4 fill-current text-primary;
          }
        }
        &.page-header-action-cart {
          @apply flex h-full justify-center py-1;
          min-width: theme('width.24'); // To reduce content shift on loading.
        }
      }
      .page-header__main-menu-divider {
        @apply hidden h-6 bg-grey-light mx-3;
        width: 1px;
      }
    }
  }
}

@screen sm {
  header.page-header {
    nav.header-menu {
      .page-header__main-menu-list {
        .page-header__main-menu-divider {
          @apply block;
        }
      }
    }
  }
}

@screen md {
  header.page-header {
    &.has-search {
      .page-header-section-left {
        @apply w-auto;
      }
    }

    nav.header-menu {
      .page-header__main-menu-list {
        li {
          &.page-header-dropdown-wrapper {
            &.page-header-dropdown-callback-wrapper {
              @apply inline-block;
            }
          }
        }
      }
    }
  }
}

@screen lg {
  header.page-header {
    &.has-search {
      .page-header-section-left {
        nav.header-menu {
          .page-header__main-menu-list {
            .page-header-search-wrapper {
              @apply flex-grow-0;
            }
          }
        }
      }
      .page-header-section-right {
        @apply flex;
      }
    }
  }
}

@screen xl {
  header.page-header {
    nav.header-menu {
      .page-header__main-menu-list {
        li {
          &.page-header-dropdown-wrapper {
            .page-header-dropdown-label {
              @apply inline-block;
              &::after {
                  @apply ml-1 text-secondary text-2xs align-middle;
                  content: "\f078";
                  font-family: "Font Awesome 5 Pro";
              }
            }
          }
        }
      }
    }
  }
}

@screen theme('customVars.sidebar.moveLinksToHeaderBreakpoint') {
  header.page-header {
    nav.header-menu {
      .page-header__main-menu-list {
        li {
          &.page-header-main-menu-item {
            @apply inline-block;
          }
        }
      }
    }
  }
}
</style>
